import React, { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import {
  CButton, CCard, CCardBody, CCol, CForm, CFormInput,
  CFormLabel, CFormSelect, CAlert, CSpinner, CInputGroup, CInputGroupText,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilLockLocked, cilLockUnlocked } from '@coreui/icons'
import { register, saveSession } from '../api/client'
import { capitalizarPalabras } from '../utils/texto'

// Países — catálogo de la tabla Pais del GX (pais.cs — webobject País del
// GX; el SDT expone PaisCodigo/PaisNombre, type_SdtPais.cs:104). Los 18
// países del seed local replican ese catálogo.
const PAISES: Array<[number, string]> = [
  [51, 'PERU'], [52, 'MEXICO'], [54, 'ARGENTINA'], [55, 'BRASIL'], [56, 'CHILE'],
  [57, 'COLOMBIA'], [502, 'GUATEMALA'], [503, 'EL SALVADOR'], [504, 'HONDURAS'],
  [505, 'NICARAGUA'], [506, 'COSTA RICA'], [507, 'PANAMA'], [508, 'ECUADOR'],
  [591, 'BOLIVIA'], [592, 'GUYANA'], [593, 'VENEZUELA'], [595, 'PARAGUAY'],
  [598, 'URUGUAY'],
]

// Banderas por código de país (selector WhatsApp del registro).
const BANDERAS: Record<number, string> = {
  51: '🇵🇪', 52: '🇲🇽', 54: '🇦🇷', 55: '🇧🇷', 56: '🇨🇱', 57: '🇨🇴',
  502: '🇬🇹', 503: '🇸🇻', 504: '🇭🇳', 505: '🇳🇮', 506: '🇨🇷', 507: '🇵🇦',
  508: '🇪🇨', 591: '🇧🇴', 592: '🇬🇾', 593: '🇻🇪', 595: '🇵🇾', 598: '🇺🇾',
}

/**
 * Registro paso 2 — detalle visual del demo CoreUI (mismo card del login).
 * Lógica de SiprinialowCode (gamregisterusermax): campos exactos, email
 * precargado y bloqueado, creación de Empresa + Usuario admin + auto-login.
 * El email llega por QUERY PARAM (link del email de verificación —
 * gamregisterusermax.cs:951-952 lee EmpresaCodigo del formulario);
 * sessionStorage queda solo como fallback de compatibilidad.
 */
const RegisterFormPage: React.FC = () => {
  const email =
    new URLSearchParams(window.location.search).get('email') ??
    sessionStorage.getItem('siprin_reg_email') ??
    ''
  const [form, setForm] = useState({
    nombreEmpresa: '', empresaCodigo: '', representante: '', paisCodigo: '57',
    celular: '', username: '', password: '', passwordConfirm: '',
    website: '', // honeypot anti-bot (campo fantasma, nadie lo llena)
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPwd, setShowPwd] = useState(false)
  const [showPwd2, setShowPwd2] = useState(false)

  if (!email) return <Navigate to="/register" replace />

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value })

  // Usuario = apodo de acceso (gamregisterusermax.cs AV17Name): se sanitiza
  // al escribir — SOLO minúsculas y sin espacios (wireframe del registro).
  const setUsername = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.toLowerCase().replace(/\s+/g, '')
    setForm({ ...form, username: v })
  }

  const coinciden = form.password.length > 0 && form.password === form.passwordConfirm

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (form.password !== form.passwordConfirm) {
      setError('Las contraseñas no coinciden')
      return
    }
    setLoading(true)
    try {
      const res = await register({
        email,
        nombreEmpresa: form.nombreEmpresa,
        empresaCodigo: form.empresaCodigo,
        // [RGM] Representante legal en MAYÚSCULAS (decisión Rafael).
        representante: form.representante.toUpperCase(),
        paisCodigo: Number(form.paisCodigo),
        celular: form.celular,
        username: form.username,
        password: form.password,
        passwordConfirm: form.passwordConfirm,
        website: form.website,
      })
      // Auto-login (GX: Useractivationmethod "A"): guardar sesión y continuar.
      // recordar=false → sessionStorage (sesión de navegador): el
      // usuario del registro no marcó "Mantener conectado" en el login.
      // [RGM] 2026-10-16: se guarda también el refreshToken — el 401 puede
      // renovar solo (paridad con el refresh automático del móvil).
      saveSession(res.tokens.accessToken, res.user, false, res.tokens.refreshToken)
      sessionStorage.removeItem('siprin_reg_email')
      window.location.href = '/home'
    } catch (err: any) {
      setError(err.message ?? 'No se pudo crear la cuenta')
      setLoading(false)
    }
  }

  return (
    <div className="bg-body-tertiary min-vh-100 d-flex align-items-center justify-content-center p-3">
      {/* Tarjeta ampliada (760px) para que el campo del celular se vea
          completo junto al selector de país con bandera (wireframe [RGM]). */}
      <div style={{ width: '100%', maxWidth: 760 }}>
        <div className="d-flex flex-column gap-4">
          <div className="text-center">
            <img src="/logo_siprinia.png" alt="SiprinIA" height={72} />
          </div>
          <CCard className="p-4">
            <CCardBody className="d-flex flex-column gap-4">
              <h2 className="h5 text-center mb-0">Crear cuenta</h2>
              <p className="text-body-secondary text-center mb-0 small">
                Complete el registro de su empresa
              </p>
              {error && <CAlert color="danger" className="mb-0">{error}</CAlert>}
              {/* Honeypot invisible (wireframe [RGM]): campo fantasma fuera de
                  pantalla; si un bot lo llena, el backend lo detecta. */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={set('website')}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: -9999, height: 0, width: 0, opacity: 0 }}
              />
              <CForm className="row gy-3" onSubmit={handleSubmit}>
                <CCol xs={12}>
                  <CAlert color="info" className="mb-0 py-2 small">
                    <strong>Registrándose con {email}</strong>
                  </CAlert>
                </CCol>
                <CCol xs={12}>
                  <CFormLabel htmlFor="reg-email2">Correo electrónico</CFormLabel>
                  <CFormInput id="reg-email2" type="email" value={email} disabled readOnly />
                </CCol>
                <CCol xs={12}>
                  <h6 className="text-body-secondary mt-2 mb-0">Información de la empresa</h6>
                </CCol>
                <CCol xs={12} md={6}>
                  <CFormLabel htmlFor="reg-representante">Representante legal</CFormLabel>
                  <CFormInput id="reg-representante" placeholder="Nombre del encargado" value={form.representante} onChange={set('representante')} style={{ textTransform: 'uppercase' }} required />
                </CCol>
                <CCol xs={12} md={6}>
                  <CFormLabel htmlFor="reg-nombre">Nombre comercial</CFormLabel>
                  {/* [RGM] 2026-10-02 (E): campo descriptivo → primera letra en
                      mayúscula (los NOMBRES formales van en mayúscula completa,
                      el representante de arriba es el caso). */}
                  <CFormInput id="reg-nombre" placeholder="Nombre de empresa" value={form.nombreEmpresa} onChange={(e) => setForm({ ...form, nombreEmpresa: capitalizarPalabras(e.target.value) })} required />
                </CCol>
                <CCol xs={12} md={6}>
                  {/* Cédula = código de la empresa (gamregisterusermax.cs:487 —
                      campo "Empresa Codigo" con placeholder "Cédula / NUIP / ID",
                      maxLength 15). */}
                  <CFormLabel htmlFor="reg-codigo">CÉDULA / DNI / ID / RUC / NIT</CFormLabel>
                  <CFormInput id="reg-codigo" placeholder="1234567" maxLength={15} value={form.empresaCodigo} onChange={set('empresaCodigo')} required />
                </CCol>
                <CCol xs={12} md={6}>
                  <CFormLabel htmlFor="reg-celular">WhatsApp</CFormLabel>
                  <div className="d-flex gap-2">
                    {/* Select de país con BANDERA + código (corto — para que
                        el número del celular se vea completo). */}
                    <CFormSelect id="reg-pais" value={form.paisCodigo} onChange={set('paisCodigo')} style={{ maxWidth: 108, flexShrink: 0 }}>
                      {PAISES.map(([cod, nombre]) => <option key={cod} value={cod}>{BANDERAS[cod] ?? ''} +{cod} · {nombre}</option>)}
                    </CFormSelect>
                    <CFormInput id="reg-celular" type="tel" inputMode="numeric" placeholder="3168750841" value={form.celular} onChange={set('celular')} required />
                  </div>
                </CCol>
                <CCol xs={12}>
                  <h6 className="text-body-secondary mt-2 mb-0">Credenciales de acceso</h6>
                </CCol>
                <CCol xs={12}>
                  <CFormLabel htmlFor="reg-username">Usuario</CFormLabel>
                  <CFormInput id="reg-username" placeholder="Nombre de usuario" value={form.username} onChange={setUsername} minLength={3} required />
                  <div className="form-text">
                    Este será tu apodo para iniciar sesión. No se puede cambiar después.
                  </div>
                </CCol>
                <CCol xs={12} md={6}>
                  <CFormLabel htmlFor="reg-password">Contraseña</CFormLabel>
                  <CInputGroup>
                    <CFormInput id="reg-password" type={showPwd ? 'text' : 'password'} placeholder="••••••••" maxLength={15} value={form.password} onChange={set('password')} required />
                    <CInputGroupText
                      as="button"
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPwd(!showPwd)}
                      style={{ cursor: 'pointer' }}
                      title={showPwd ? 'Ocultar contraseña' : 'Ver contraseña'}
                    >
                      <CIcon icon={showPwd ? cilLockUnlocked : cilLockLocked} />
                    </CInputGroupText>
                  </CInputGroup>
                  {/* [RGM] 2026-11-04 (decisión Rafael): clave de 1 a 15
                      caracteres (UsuarioClave es char(15) GX). */}
                  <small className="text-body-secondary">Máximo 15 caracteres</small>
                </CCol>
                <CCol xs={12} md={6}>
                  <CFormLabel htmlFor="reg-password2">Confirmar contraseña</CFormLabel>
                  <CInputGroup>
                    <CFormInput id="reg-password2" type={showPwd2 ? 'text' : 'password'} placeholder="••••••••" maxLength={15} value={form.passwordConfirm} onChange={set('passwordConfirm')} required />
                    <CInputGroupText
                      as="button"
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPwd2(!showPwd2)}
                      style={{ cursor: 'pointer' }}
                      title={showPwd2 ? 'Ocultar contraseña' : 'Ver contraseña'}
                    >
                      <CIcon icon={showPwd2 ? cilLockUnlocked : cilLockLocked} />
                    </CInputGroupText>
                  </CInputGroup>
                  <small className="text-body-secondary">Máximo 15 caracteres</small>
                  {form.passwordConfirm.length > 0 && (
                    <div className={`small mt-1 ${coinciden ? 'text-success' : 'text-danger'}`}>
                      {coinciden ? 'Las contraseñas coinciden' : 'Las contraseñas no coinciden'}
                    </div>
                  )}
                </CCol>
                <CCol xs={12}>
                  <CButton color="primary" type="submit" disabled={loading} className="w-100">
                    {loading ? <CSpinner size="sm" /> : 'Crear cuenta'}
                  </CButton>
                </CCol>
              </CForm>
            </CCardBody>
          </CCard>
          <div className="text-center text-body-secondary">
            ¿Ya tienes una cuenta? <Link to="/login">Iniciar sesión</Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisterFormPage
